import { useState, useRef, useEffect, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Slider } from "@/components/ui/slider";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { 
  Zap, Upload, FileText, Download, Moon, Sun, Info, RotateCcw, 
  AlignLeft, AlignCenter, AlignRight, AlignJustify,
  Type, FileDown, Eye, EyeOff, Lock, Droplet, Hash,
  Bold, Italic, Underline, List, ListOrdered, Heading1, Heading2, Palette
} from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { toast } from "sonner";
import { Toaster } from "@/components/ui/sonner";
import { jsPDF } from "jspdf";
import ToolsNav from "@/components/ToolsNav";
import { RenderCMSContent } from "@/components/CMSRenderer";
import { AdPlacement, AdContainer, ResponsiveAd, StickyMobileAd } from "@/components/AdPlacement";

export default function TextToPdf() {
  const [content, setContent] = useState("");
  const [fileName, setFileName] = useState("document");
  const [fontSize, setFontSize] = useState([12]);
  const [fontFamily, setFontFamily] = useState("helvetica");
  const [pageSize, setPageSize] = useState("a4");
  const [textAlign, setTextAlign] = useState("left");
  const [lineHeight, setLineHeight] = useState([1.5]);
  const [margins, setMargins] = useState([20]);
  const [isDragging, setIsDragging] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [cmsContent, setCmsContent] = useState(null);
  
  // Color options
  const [textColor, setTextColor] = useState("#000000");
  const [backgroundColor, setBackgroundColor] = useState("#ffffff");
  
  // Page numbers & headers
  const [showPageNumbers, setShowPageNumbers] = useState(false);
  const [pageNumberPosition, setPageNumberPosition] = useState("bottom-center");
  const [headerText, setHeaderText] = useState("");
  const [footerText, setFooterText] = useState("");
  
  // Watermark
  const [watermarkEnabled, setWatermarkEnabled] = useState(false);
  const [watermarkText, setWatermarkText] = useState("");
  const [watermarkOpacity, setWatermarkOpacity] = useState([30]);
  const [watermarkAngle, setWatermarkAngle] = useState([-45]);
  
  // Password protection
  const [passwordEnabled, setPasswordEnabled] = useState(false);
  const [pdfPassword, setPdfPassword] = useState("");
  
  const [darkMode, setDarkMode] = useState(() => {
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem('darkMode');
      if (saved !== null) return JSON.parse(saved);
      return window.matchMedia('(prefers-color-scheme: dark)').matches;
    }
    return false;
  });
  
  const fileInputRef = useRef(null);
  const editorRef = useRef(null);
  const API_URL = process.env.REACT_APP_BACKEND_URL;

  // Apply dark mode
  useEffect(() => {
    if (darkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
    localStorage.setItem('darkMode', JSON.stringify(darkMode));
  }, [darkMode]);

  // Fetch CMS content
  useEffect(() => {
    const fetchContent = async () => {
      try {
        const response = await fetch(`${API_URL}/api/pages/slug/text-to-pdf`);
        if (response.ok) {
          const data = await response.json();
          setCmsContent(data);
          
          if (data.seo) {
            if (data.seo.meta_title) document.title = data.seo.meta_title;
            if (data.seo.meta_description) {
              let meta = document.querySelector('meta[name="description"]');
              if (!meta) {
                meta = document.createElement('meta');
                meta.name = 'description';
                document.head.appendChild(meta);
              }
              meta.content = data.seo.meta_description;
            }
          }
        }
      } catch (error) {
        console.log('No CMS content found');
      }
    };
    fetchContent();
  }, [API_URL]);

  // Rich text formatting commands - save/restore selection to handle toolbar clicks
  const savedSelection = useRef(null);
  
  const saveSelection = () => {
    const selection = window.getSelection();
    if (selection && selection.rangeCount > 0) {
      savedSelection.current = selection.getRangeAt(0);
    }
  };
  
  const restoreSelection = () => {
    if (savedSelection.current && editorRef.current) {
      editorRef.current.focus();
      const selection = window.getSelection();
      selection.removeAllRanges();
      selection.addRange(savedSelection.current);
    }
  };
  
  const execCommand = (command, value = null) => {
    // Restore selection before executing command
    restoreSelection();
    
    // Execute the command
    document.execCommand(command, false, value);
    
    // Update content state
    updateContent();
    
    // Save new selection
    saveSelection();
  };

  const updateContent = () => {
    if (editorRef.current) {
      setContent(editorRef.current.innerHTML);
    }
  };
  
  // Save selection when editor loses focus or on selection change
  const handleEditorBlur = () => {
    saveSelection();
  };
  
  const handleEditorSelect = () => {
    saveSelection();
  };

  // Convert hex or rgb color to RGB object
  const hexToRgb = (color) => {
    if (!color) return { r: 0, g: 0, b: 0 };
    
    // Handle rgb() format
    const rgbMatch = color.match(/rgb\s*\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)\s*\)/i);
    if (rgbMatch) {
      return {
        r: parseInt(rgbMatch[1], 10),
        g: parseInt(rgbMatch[2], 10),
        b: parseInt(rgbMatch[3], 10)
      };
    }
    
    // Handle hex format
    const hex = color.replace('#', '');
    const result = /^([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result ? {
      r: parseInt(result[1], 16),
      g: parseInt(result[2], 16),
      b: parseInt(result[3], 16)
    } : { r: 0, g: 0, b: 0 };
  };

  // Parse HTML and extract formatted segments with styling info
  const parseHtmlToSegments = (html) => {
    if (!html) return [];
    
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = html;
    
    const segments = [];
    let listCounter = 0;
    
    const processNode = (node, styles = { bold: false, italic: false, underline: false, heading: null, color: null }) => {
      if (node.nodeType === Node.TEXT_NODE) {
        const text = node.textContent;
        if (text && text.trim()) {
          segments.push({
            text: text,
            ...styles
          });
        } else if (text && /\s+/.test(text) && segments.length > 0) {
          // Preserve spaces between inline elements
          segments.push({ text: ' ', ...styles });
        }
        return;
      }
      
      if (node.nodeType !== Node.ELEMENT_NODE) return;
      
      const tag = node.tagName.toLowerCase();
      const newStyles = { ...styles };
      
      // Track formatting
      if (tag === 'b' || tag === 'strong') newStyles.bold = true;
      if (tag === 'i' || tag === 'em') newStyles.italic = true;
      if (tag === 'u') newStyles.underline = true;
      if (tag === 'h1') newStyles.heading = 'h1';
      if (tag === 'h2') newStyles.heading = 'h2';
      
      // Extract color from font tag or span style
      if (tag === 'font' && node.getAttribute('color')) {
        newStyles.color = node.getAttribute('color');
      }
      if (node.style && node.style.color) {
        newStyles.color = node.style.color;
      }
      
      // Handle list items
      if (tag === 'ul' || tag === 'ol') {
        listCounter = 0;
      }
      
      if (tag === 'li') {
        listCounter++;
        const parent = node.parentElement;
        const prefix = parent && parent.tagName.toLowerCase() === 'ol' 
          ? `${listCounter}. ` 
          : '• ';
        segments.push({ text: prefix, ...newStyles });
      }
      
      // Process children
      node.childNodes.forEach(child => processNode(child, newStyles));
      
      // Add line breaks after block elements
      if (['p', 'div', 'h1', 'h2', 'h3', 'li', 'br'].includes(tag)) {
        segments.push({ text: '\n', lineBreak: true });
      }
    };
    
    processNode(tempDiv);
    
    // Clean up: merge consecutive line breaks, trim
    const cleaned = [];
    let prevWasBreak = false;
    
    segments.forEach(seg => {
      if (seg.lineBreak) {
        if (!prevWasBreak) {
          cleaned.push(seg);
          prevWasBreak = true;
        }
      } else {
        cleaned.push(seg);
        prevWasBreak = false;
      }
    });
    
    return cleaned;
  };

  // Extract plain text from HTML while preserving line breaks (for word count, etc.)
  const getPlainText = (html) => {
    if (!html) return '';
    
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = html;
    
    tempDiv.innerHTML = tempDiv.innerHTML.replace(/<br\s*\/?>/gi, '\n');
    tempDiv.innerHTML = tempDiv.innerHTML.replace(/<\/(p|div|h1|h2|h3|h4|h5|h6|li)>/gi, '\n');
    
    const listItems = tempDiv.querySelectorAll('li');
    listItems.forEach((li, index) => {
      const parent = li.parentElement;
      if (parent && parent.tagName.toLowerCase() === 'ul') {
        li.innerHTML = '• ' + li.innerHTML;
      } else if (parent && parent.tagName.toLowerCase() === 'ol') {
        li.innerHTML = (index + 1) + '. ' + li.innerHTML;
      }
    });
    
    let text = tempDiv.textContent || tempDiv.innerText || '';
    text = text.replace(/\n{3,}/g, '\n\n');
    text = text.split('\n').map(line => line.trim()).join('\n');
    
    return text;
  };

  const generatePDF = useCallback((forPreview = false) => {
    const plainText = getPlainText(content);
    
    if (!plainText.trim()) {
      toast.error("Please enter some text first");
      return null;
    }
    
    try {
      // Page size configurations
      const pageSizes = {
        a4: { width: 210, height: 297 },
        letter: { width: 215.9, height: 279.4 },
        legal: { width: 215.9, height: 355.6 },
        a3: { width: 297, height: 420 },
        a5: { width: 148, height: 210 }
      };
      
      const page = pageSizes[pageSize];
      const margin = margins[0];
      const contentWidth = page.width - (margin * 2);
      const headerFooterMargin = 10;
      
      // Create PDF with optional encryption
      const docOptions = {
        orientation: 'portrait',
        unit: 'mm',
        format: pageSize
      };
      
      if (passwordEnabled && pdfPassword && !forPreview) {
        docOptions.encryption = {
          userPassword: pdfPassword,
          ownerPassword: pdfPassword,
          userPermissions: ['print', 'copy']
        };
      }
      
      const doc = new jsPDF(docOptions);
      
      // Set background color
      if (backgroundColor !== '#ffffff') {
        const bgColor = hexToRgb(backgroundColor);
        doc.setFillColor(bgColor.r, bgColor.g, bgColor.b);
        doc.rect(0, 0, page.width, page.height, 'F');
      }
      
      // Set text color
      const txtColor = hexToRgb(textColor);
      doc.setTextColor(txtColor.r, txtColor.g, txtColor.b);
      
      // Base font size
      const baseFontSize = fontSize[0];
      
      // Calculate line height in mm
      const lineHeightMm = (baseFontSize * 0.3528) * lineHeight[0];
      
      // Parse HTML to get formatted segments
      const segments = parseHtmlToSegments(content);
      
      // If no segments found, fall back to plain text rendering
      if (segments.length === 0) {
        doc.setFont(fontFamily, 'normal');
        doc.setFontSize(baseFontSize);
        const lines = doc.splitTextToSize(plainText, contentWidth);
        let fallbackY = margin;
        lines.forEach(line => {
          if (fallbackY + lineHeightMm > page.height - margin) {
            doc.addPage();
            fallbackY = margin;
          }
          doc.text(line, margin, fallbackY);
          fallbackY += lineHeightMm;
        });
        return doc;
      }
      
      // Helper to set font style based on segment properties
      const setFontStyle = (segment) => {
        let style = 'normal';
        let size = baseFontSize;
        
        if (segment.heading === 'h1') {
          size = baseFontSize * 2; // H1 is 2x base size
          style = 'bold';
        } else if (segment.heading === 'h2') {
          size = baseFontSize * 1.5; // H2 is 1.5x base size
          style = 'bold';
        } else if (segment.bold && segment.italic) {
          // jsPDF standard fonts use 'bolditalic' for Helvetica/Times, 'boldoblique' for Courier
          style = fontFamily === 'courier' ? 'boldoblique' : 'bolditalic';
        } else if (segment.bold) {
          style = 'bold';
        } else if (segment.italic) {
          // jsPDF standard fonts use 'italic' for Helvetica/Times, 'oblique' for Courier
          style = fontFamily === 'courier' ? 'oblique' : 'italic';
        }
        
        try {
          doc.setFont(fontFamily, style);
        } catch (e) {
          // Fallback to normal if style not available
          doc.setFont(fontFamily, 'normal');
        }
        doc.setFontSize(size);
        
        return { size, needsUnderline: segment.underline };
      };
      
      // Calculate starting Y position (after header if present)
      let startY = margin;
      if (headerText) {
        startY = margin + 10;
      }
      
      let y = startY;
      let x = margin;
      let pageNum = 1;
      
      // Estimate total pages (rough)
      const estimatedLines = Math.ceil(plainText.length / 80);
      const linesPerPage = Math.floor((page.height - margin * 2 - (headerText ? 10 : 0) - (footerText || showPageNumbers ? 15 : 0)) / lineHeightMm);
      const totalPages = Math.max(1, Math.ceil(estimatedLines / linesPerPage));
      
      // Function to add watermark
      const addWatermark = () => {
        if (watermarkEnabled && watermarkText) {
          doc.saveGraphicsState();
          const watermarkColor = hexToRgb(textColor);
          doc.setTextColor(watermarkColor.r, watermarkColor.g, watermarkColor.b);
          doc.setGState(new doc.GState({ opacity: watermarkOpacity[0] / 100 }));
          doc.setFontSize(48);
          doc.setFont(fontFamily, 'normal');
          
          const centerX = page.width / 2;
          const centerY = page.height / 2;
          
          doc.text(watermarkText, centerX, centerY, {
            align: 'center',
            angle: watermarkAngle[0]
          });
          
          doc.restoreGraphicsState();
          doc.setFontSize(baseFontSize);
          doc.setTextColor(txtColor.r, txtColor.g, txtColor.b);
        }
      };
      
      // Function to add header
      const addHeader = () => {
        if (headerText) {
          doc.setFont(fontFamily, 'normal');
          doc.setFontSize(10);
          doc.text(headerText, page.width / 2, headerFooterMargin, { align: 'center' });
          doc.setFontSize(baseFontSize);
        }
      };
      
      // Function to add footer and page numbers
      const addFooter = (currentPage, total) => {
        const footerY = page.height - headerFooterMargin;
        doc.setFont(fontFamily, 'normal');
        
        if (footerText) {
          doc.setFontSize(10);
          doc.text(footerText, page.width / 2, footerY - 5, { align: 'center' });
        }
        
        if (showPageNumbers) {
          doc.setFontSize(10);
          const pageText = `Page ${currentPage} of ${total}`;
          
          let px = page.width / 2;
          let align = 'center';
          
          if (pageNumberPosition === 'bottom-left') {
            px = margin;
            align = 'left';
          } else if (pageNumberPosition === 'bottom-right') {
            px = page.width - margin;
            align = 'right';
          }
          
          doc.text(pageText, px, footerY, { align });
        }
        doc.setFontSize(baseFontSize);
      };
      
      // Function to check and handle page break
      const checkPageBreak = (neededHeight) => {
        const bottomMargin = margin + (footerText || showPageNumbers ? 15 : 0);
        if (y + neededHeight > page.height - bottomMargin) {
          addFooter(pageNum, totalPages);
          doc.addPage();
          pageNum++;
          
          // Add background to new page
          if (backgroundColor !== '#ffffff') {
            const bgColor = hexToRgb(backgroundColor);
            doc.setFillColor(bgColor.r, bgColor.g, bgColor.b);
            doc.rect(0, 0, page.width, page.height, 'F');
          }
          doc.setTextColor(txtColor.r, txtColor.g, txtColor.b);
          
          addWatermark();
          addHeader();
          y = startY;
          x = margin;
        }
      };
      
      // Add first page elements
      addWatermark();
      addHeader();
      
      // Process each segment
      for (let i = 0; i < segments.length; i++) {
        const segment = segments[i];
        
        // Handle line breaks
        if (segment.lineBreak) {
          y += lineHeightMm;
          x = margin;
          checkPageBreak(lineHeightMm);
          continue;
        }
        
        // Skip empty text
        if (!segment.text || segment.text === '') continue;
        
        // Set font style for this segment
        const { size, needsUnderline } = setFontStyle(segment);
        const currentLineHeight = (size * 0.3528) * lineHeight[0];
        
        // Set text color for this segment (use segment color or default)
        if (segment.color) {
          const segmentColor = hexToRgb(segment.color);
          doc.setTextColor(segmentColor.r, segmentColor.g, segmentColor.b);
        } else {
          doc.setTextColor(txtColor.r, txtColor.g, txtColor.b);
        }
        
        // Check if we need page break
        checkPageBreak(currentLineHeight);
        
        // Handle text that needs to wrap
        const text = segment.text;
        const availableWidth = contentWidth - (x - margin);
        
        // Split text to fit available width
        const words = text.split(' ');
        let currentLine = '';
        
        for (let w = 0; w < words.length; w++) {
          const word = words[w];
          const testLine = currentLine ? currentLine + ' ' + word : word;
          const testWidth = doc.getTextWidth(testLine);
          
          if (testWidth > availableWidth && currentLine) {
            // Draw current line
            let drawX = x;
            if (textAlign === 'center') {
              drawX = margin + (contentWidth - doc.getTextWidth(currentLine)) / 2;
            } else if (textAlign === 'right') {
              drawX = page.width - margin - doc.getTextWidth(currentLine);
            }
            
            doc.text(currentLine, drawX, y);
            
            // Draw underline if needed
            if (needsUnderline) {
              const textWidth = doc.getTextWidth(currentLine);
              doc.setLineWidth(0.2);
              doc.line(drawX, y + 1, drawX + textWidth, y + 1);
            }
            
            // Move to next line
            y += currentLineHeight;
            x = margin;
            currentLine = word;
            
            checkPageBreak(currentLineHeight);
          } else {
            currentLine = testLine;
          }
        }
        
        // Draw remaining text
        if (currentLine) {
          let drawX = x;
          if (textAlign === 'center' && x === margin) {
            drawX = margin + (contentWidth - doc.getTextWidth(currentLine)) / 2;
          } else if (textAlign === 'right' && x === margin) {
            drawX = page.width - margin - doc.getTextWidth(currentLine);
          }
          
          doc.text(currentLine, drawX, y);
          
          // Draw underline if needed
          if (needsUnderline) {
            const textWidth = doc.getTextWidth(currentLine);
            doc.setLineWidth(0.2);
            doc.line(drawX, y + 1, drawX + textWidth, y + 1);
          }
          
          // Update x position for next inline segment
          x = drawX + doc.getTextWidth(currentLine);
        }
      }
      
      // Add footer to last page
      addFooter(pageNum, totalPages);
      
      return doc;
    } catch (error) {
      console.error("PDF generation error:", error);
      toast.error("Failed to generate PDF");
      return null;
    }
  }, [content, pageSize, margins, fontFamily, fontSize, lineHeight, textAlign, textColor, backgroundColor, headerText, footerText, showPageNumbers, pageNumberPosition, watermarkEnabled, watermarkText, watermarkOpacity, watermarkAngle, passwordEnabled, pdfPassword]);

  const handleDownload = () => {
    setIsGenerating(true);
    
    try {
      const doc = generatePDF(false);
      if (doc) {
        doc.save(`${fileName || 'document'}.pdf`);
        toast.success("PDF downloaded successfully!");
      }
    } finally {
      setIsGenerating(false);
    }
  };

  const handlePreview = () => {
    try {
      const doc = generatePDF(true);
      if (doc) {
        const blob = doc.output('blob');
        const url = URL.createObjectURL(blob);
        
        if (previewUrl) {
          URL.revokeObjectURL(previewUrl);
        }
        
        setPreviewUrl(url);
        setShowPreview(true);
      }
    } catch (error) {
      console.error('Preview generation error:', error);
      toast.error('Failed to generate preview');
    }
  };

  const handleFileUpload = async (file) => {
    if (!file) return;
    
    if (!file.name.endsWith('.txt') && file.type !== 'text/plain') {
      toast.error("Please upload a .txt file");
      return;
    }
    
    try {
      const text = await file.text();
      const htmlContent = text.split('\n').map(line => line || '<br>').join('<br>');
      setContent(htmlContent);
      if (editorRef.current) {
        editorRef.current.innerHTML = htmlContent;
      }
      setFileName(file.name.replace('.txt', ''));
      toast.success("Text file loaded!");
    } catch (error) {
      toast.error("Failed to read file");
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      handleFileUpload(file);
    }
    e.target.value = '';
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    
    const file = e.dataTransfer.files[0];
    if (file) {
      handleFileUpload(file);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handlePaste = async () => {
    try {
      const clipboardText = await navigator.clipboard.readText();
      const htmlContent = clipboardText.split('\n').map(line => line || '<br>').join('<br>');
      setContent(htmlContent);
      if (editorRef.current) {
        editorRef.current.innerHTML = htmlContent;
      }
      toast.success("Text pasted from clipboard!");
    } catch (error) {
      toast.error("Failed to paste from clipboard");
    }
  };

  const handleReset = () => {
    setContent("");
    if (editorRef.current) {
      editorRef.current.innerHTML = "";
    }
    setFileName("document");
    setFontSize([12]);
    setFontFamily("helvetica");
    setPageSize("a4");
    setTextAlign("left");
    setLineHeight([1.5]);
    setMargins([20]);
    setTextColor("#000000");
    setBackgroundColor("#ffffff");
    setShowPageNumbers(false);
    setHeaderText("");
    setFooterText("");
    setWatermarkEnabled(false);
    setWatermarkText("");
    setWatermarkOpacity([30]);
    setPasswordEnabled(false);
    setPdfPassword("");
    setShowPreview(false);
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
      setPreviewUrl(null);
    }
    toast.info("Reset complete");
  };

  const plainText = getPlainText(content);
  const wordCount = plainText.trim() ? plainText.trim().split(/\s+/).length : 0;
  const charCount = plainText.length;

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900 transition-colors duration-300">
      <Toaster position="top-center" theme={darkMode ? "dark" : "light"} />
      
      {/* Header */}
      <header className="border-b border-slate-200 dark:border-slate-700 bg-white/70 dark:bg-slate-800/70 backdrop-blur-md sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4 md:py-6">
          <div className="flex items-center justify-between">
            <a href="/" className="flex items-center gap-3 hover:opacity-80 transition-opacity">
              <div className="w-10 h-10 rounded-full bg-blue-600 flex items-center justify-center">
                <Zap className="w-5 h-5 text-white" />
              </div>
              <h1 className="text-2xl md:text-3xl font-bold tracking-tight text-slate-900 dark:text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>
                KayTool
              </h1>
            </a>
            <div className="flex items-center gap-3">
              <ToolsNav />
              <Badge variant="secondary" className="hidden md:flex items-center gap-1 px-3 py-1 dark:bg-slate-700 dark:text-slate-200">
                <Info className="w-3 h-3" />
                100% Local Processing
              </Badge>
              <Button
                variant="outline"
                size="icon"
                onClick={() => setDarkMode(prev => !prev)}
                className="rounded-full w-10 h-10 border-slate-200 dark:border-slate-600 dark:bg-slate-700"
              >
                {darkMode ? <Sun className="w-5 h-5 text-yellow-500" /> : <Moon className="w-5 h-5 text-slate-600" />}
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Ad Placement - Above the Fold (Below Header) */}
      <AdContainer className="mt-4 mb-0">
        <ResponsiveAd slot="above-fold-header" mobileSize="mobile-banner" desktopSize="banner" />
      </AdContainer>

      <main className="container mx-auto px-4 py-8 md:py-12">
        {/* Title Section */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-100 dark:bg-emerald-900/30 rounded-full mb-4">
            <FileDown className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
            <span className="text-sm font-semibold text-emerald-700 dark:text-emerald-400">Text to PDF</span>
          </div>
          <h2 className="text-3xl md:text-5xl font-bold mb-4 tracking-tight text-slate-900 dark:text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Convert Text to PDF
          </h2>
          <p className="text-base md:text-lg text-slate-600 dark:text-slate-400 max-w-2xl mx-auto">
            Create professional PDF documents with rich formatting, watermarks, and password protection.
          </p>
        </div>

        <div className="max-w-6xl mx-auto">
          <div className={`grid gap-6 ${showPreview ? 'lg:grid-cols-2' : 'grid-cols-1'}`}>
            {/* Editor Section */}
            <div className="space-y-6">
              {/* Input Methods */}
              <Card className="border-slate-200 dark:border-slate-700 dark:bg-slate-800">
                <CardContent className="p-4">
                  <div className="flex flex-wrap items-center gap-3 mb-4">
                    <Button variant="outline" onClick={() => fileInputRef.current?.click()} className="rounded-full" data-testid="upload-txt-button">
                      <Upload className="w-4 h-4 mr-2" />
                      Upload TXT
                    </Button>
                    <Button variant="outline" onClick={handlePaste} className="rounded-full">
                      <Type className="w-4 h-4 mr-2" />
                      Paste
                    </Button>
                    <Button
                      variant={showPreview ? "default" : "outline"}
                      onClick={handlePreview}
                      className={`rounded-full ${showPreview ? 'bg-emerald-600 hover:bg-emerald-700' : ''}`}
                      data-testid="preview-button"
                    >
                      {showPreview ? <EyeOff className="w-4 h-4 mr-2" /> : <Eye className="w-4 h-4 mr-2" />}
                      {showPreview ? 'Hide Preview' : 'Preview'}
                    </Button>
                    <div className="flex-1" />
                    <Badge variant="secondary" className="dark:bg-slate-700">{wordCount} words</Badge>
                    <Badge variant="secondary" className="dark:bg-slate-700">{charCount} chars</Badge>
                  </div>

                  {/* Formatting Toolbar */}
                  <div className="flex flex-wrap items-center gap-1 p-2 bg-slate-100 dark:bg-slate-700 rounded-t-lg border border-b-0 border-slate-200 dark:border-slate-600">
                    <Button variant="ghost" size="sm" onMouseDown={(e) => { e.preventDefault(); execCommand('bold'); }} title="Bold">
                      <Bold className="w-4 h-4" />
                    </Button>
                    <Button variant="ghost" size="sm" onMouseDown={(e) => { e.preventDefault(); execCommand('italic'); }} title="Italic">
                      <Italic className="w-4 h-4" />
                    </Button>
                    <Button variant="ghost" size="sm" onMouseDown={(e) => { e.preventDefault(); execCommand('underline'); }} title="Underline">
                      <Underline className="w-4 h-4" />
                    </Button>
                    <div className="w-px h-6 bg-slate-300 dark:bg-slate-600 mx-1" />
                    <Button variant="ghost" size="sm" onMouseDown={(e) => { e.preventDefault(); execCommand('formatBlock', '<h1>'); }} title="Heading 1">
                      <Heading1 className="w-4 h-4" />
                    </Button>
                    <Button variant="ghost" size="sm" onMouseDown={(e) => { e.preventDefault(); execCommand('formatBlock', '<h2>'); }} title="Heading 2">
                      <Heading2 className="w-4 h-4" />
                    </Button>
                    <div className="w-px h-6 bg-slate-300 dark:bg-slate-600 mx-1" />
                    <Button variant="ghost" size="sm" onMouseDown={(e) => { e.preventDefault(); execCommand('insertUnorderedList'); }} title="Bullet List">
                      <List className="w-4 h-4" />
                    </Button>
                    <Button variant="ghost" size="sm" onMouseDown={(e) => { e.preventDefault(); execCommand('insertOrderedList'); }} title="Numbered List">
                      <ListOrdered className="w-4 h-4" />
                    </Button>
                    <div className="w-px h-6 bg-slate-300 dark:bg-slate-600 mx-1" />
                    <Button variant="ghost" size="sm" onMouseDown={(e) => { e.preventDefault(); execCommand('justifyLeft'); }} title="Align Left">
                      <AlignLeft className="w-4 h-4" />
                    </Button>
                    <Button variant="ghost" size="sm" onMouseDown={(e) => { e.preventDefault(); execCommand('justifyCenter'); }} title="Align Center">
                      <AlignCenter className="w-4 h-4" />
                    </Button>
                    <Button variant="ghost" size="sm" onMouseDown={(e) => { e.preventDefault(); execCommand('justifyRight'); }} title="Align Right">
                      <AlignRight className="w-4 h-4" />
                    </Button>
                    <div className="w-px h-6 bg-slate-300 dark:bg-slate-600 mx-1" />
                    {/* Text Color Picker */}
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button variant="ghost" size="sm" title="Text Color" className="relative">
                          <Palette className="w-4 h-4" />
                          <div className="absolute bottom-1 left-1/2 -translate-x-1/2 w-3 h-1 rounded-sm" style={{ backgroundColor: textColor }} />
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-3" align="start">
                        <div className="space-y-3">
                          <p className="text-sm font-medium text-slate-700 dark:text-slate-300">Text Color</p>
                          <div className="grid grid-cols-8 gap-1">
                            {[
                              '#000000', '#374151', '#6B7280', '#9CA3AF',
                              '#EF4444', '#F97316', '#F59E0B', '#EAB308',
                              '#84CC16', '#22C55E', '#10B981', '#14B8A6',
                              '#06B6D4', '#0EA5E9', '#3B82F6', '#6366F1',
                              '#8B5CF6', '#A855F7', '#D946EF', '#EC4899',
                              '#F43F5E', '#FFFFFF', '#1E40AF', '#166534'
                            ].map((color) => (
                              <button
                                key={color}
                                className={`w-6 h-6 rounded border-2 ${color === '#FFFFFF' ? 'border-slate-300' : 'border-transparent'} hover:scale-110 transition-transform`}
                                style={{ backgroundColor: color }}
                                onMouseDown={(e) => {
                                  e.preventDefault();
                                  restoreSelection();
                                  execCommand('foreColor', color);
                                }}
                                title={color}
                              />
                            ))}
                          </div>
                          <div className="flex items-center gap-2">
                            <input
                              type="color"
                              className="w-8 h-8 rounded cursor-pointer"
                              defaultValue="#000000"
                              onMouseDown={(e) => e.stopPropagation()}
                              onChange={(e) => {
                                restoreSelection();
                                execCommand('foreColor', e.target.value);
                              }}
                            />
                            <span className="text-xs text-slate-500">Custom color</span>
                          </div>
                        </div>
                      </PopoverContent>
                    </Popover>
                  </div>

                  {/* Rich Text Editor */}
                  <div
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                    className={`relative ${isDragging ? 'ring-2 ring-emerald-500 ring-offset-2' : ''}`}
                  >
                    <div
                      ref={editorRef}
                      contentEditable
                      onInput={updateContent}
                      onBlur={handleEditorBlur}
                      onSelect={handleEditorSelect}
                      onMouseUp={handleEditorSelect}
                      onKeyUp={handleEditorSelect}
                      className="min-h-[250px] p-4 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-600 rounded-b-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 prose dark:prose-invert max-w-none"
                      data-placeholder="Type or paste your text here... Use the toolbar for formatting."
                      suppressContentEditableWarning
                    />
                    {!content && (
                      <p className="absolute top-4 left-4 text-slate-400 pointer-events-none">
                        Type or paste your text here... Use the toolbar for formatting.
                      </p>
                    )}
                    {isDragging && (
                      <div className="absolute inset-0 bg-emerald-50 dark:bg-emerald-900/30 border-2 border-dashed border-emerald-500 rounded-md flex items-center justify-center z-10">
                        <p className="text-emerald-600 dark:text-emerald-400 font-medium">Drop your .txt file here</p>
                      </div>
                    )}
                  </div>
                  
                  <input ref={fileInputRef} type="file" accept=".txt,text/plain" onChange={handleFileSelect} className="hidden" />
                </CardContent>
              </Card>

              {/* Settings Tabs */}
              <Card className="border-slate-200 dark:border-slate-700 dark:bg-slate-800">
                <CardContent className="p-4">
                  <Tabs defaultValue="basic" className="w-full">
                    <TabsList className="grid grid-cols-4 mb-4">
                      <TabsTrigger value="basic">Basic</TabsTrigger>
                      <TabsTrigger value="colors">Colors</TabsTrigger>
                      <TabsTrigger value="header">Header/Footer</TabsTrigger>
                      <TabsTrigger value="advanced">Advanced</TabsTrigger>
                    </TabsList>

                    {/* Basic Settings */}
                    <TabsContent value="basic" className="space-y-4">
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                          <label className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-2 block">File Name</label>
                          <Input value={fileName} onChange={(e) => setFileName(e.target.value)} placeholder="document" className="bg-white dark:bg-slate-900" />
                        </div>
                        <div>
                          <label className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-2 block">Page Size</label>
                          <Select value={pageSize} onValueChange={setPageSize}>
                            <SelectTrigger className="bg-white dark:bg-slate-900"><SelectValue /></SelectTrigger>
                            <SelectContent className="dark:bg-slate-800">
                              <SelectItem value="a4">A4</SelectItem>
                              <SelectItem value="letter">Letter</SelectItem>
                              <SelectItem value="legal">Legal</SelectItem>
                              <SelectItem value="a3">A3</SelectItem>
                              <SelectItem value="a5">A5</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <label className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-2 block">Font</label>
                          <Select value={fontFamily} onValueChange={setFontFamily}>
                            <SelectTrigger className="bg-white dark:bg-slate-900"><SelectValue /></SelectTrigger>
                            <SelectContent className="dark:bg-slate-800">
                              <SelectItem value="helvetica">Helvetica</SelectItem>
                              <SelectItem value="times">Times New Roman</SelectItem>
                              <SelectItem value="courier">Courier</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                      
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                          <div className="flex justify-between items-center mb-2">
                            <label className="text-sm font-medium text-slate-700 dark:text-slate-300">Font Size</label>
                            <Badge variant="secondary" className="dark:bg-slate-700">{fontSize[0]}pt</Badge>
                          </div>
                          <Slider value={fontSize} onValueChange={setFontSize} min={8} max={24} step={1} />
                        </div>
                        <div>
                          <div className="flex justify-between items-center mb-2">
                            <label className="text-sm font-medium text-slate-700 dark:text-slate-300">Line Spacing</label>
                            <Badge variant="secondary" className="dark:bg-slate-700">{lineHeight[0]}x</Badge>
                          </div>
                          <Slider value={lineHeight} onValueChange={setLineHeight} min={1} max={3} step={0.1} />
                        </div>
                        <div>
                          <div className="flex justify-between items-center mb-2">
                            <label className="text-sm font-medium text-slate-700 dark:text-slate-300">Margins</label>
                            <Badge variant="secondary" className="dark:bg-slate-700">{margins[0]}mm</Badge>
                          </div>
                          <Slider value={margins} onValueChange={setMargins} min={10} max={40} step={5} />
                        </div>
                      </div>

                      <div>
                        <label className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-2 block">Text Alignment (PDF)</label>
                        <div className="flex gap-2">
                          {[
                            { value: 'left', icon: AlignLeft },
                            { value: 'center', icon: AlignCenter },
                            { value: 'right', icon: AlignRight },
                            { value: 'justify', icon: AlignJustify }
                          ].map(({ value, icon: Icon }) => (
                            <Button
                              key={value}
                              variant={textAlign === value ? 'default' : 'outline'}
                              size="sm"
                              onClick={() => setTextAlign(value)}
                              className={textAlign === value ? 'bg-emerald-600 hover:bg-emerald-700' : ''}
                            >
                              <Icon className="w-4 h-4" />
                            </Button>
                          ))}
                        </div>
                      </div>
                    </TabsContent>

                    {/* Colors Settings */}
                    <TabsContent value="colors" className="space-y-4">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-2 flex items-center gap-2">
                            <Type className="w-4 h-4" /> Text Color
                          </label>
                          <div className="flex items-center gap-3">
                            <input type="color" value={textColor} onChange={(e) => setTextColor(e.target.value)} className="w-12 h-10 rounded cursor-pointer border-2 border-slate-200 dark:border-slate-600" />
                            <Input value={textColor} onChange={(e) => setTextColor(e.target.value)} className="flex-1 bg-white dark:bg-slate-900 uppercase" maxLength={7} />
                          </div>
                        </div>
                        <div>
                          <label className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-2 flex items-center gap-2">
                            <Droplet className="w-4 h-4" /> Background Color
                          </label>
                          <div className="flex items-center gap-3">
                            <input type="color" value={backgroundColor} onChange={(e) => setBackgroundColor(e.target.value)} className="w-12 h-10 rounded cursor-pointer border-2 border-slate-200 dark:border-slate-600" />
                            <Input value={backgroundColor} onChange={(e) => setBackgroundColor(e.target.value)} className="flex-1 bg-white dark:bg-slate-900 uppercase" maxLength={7} />
                          </div>
                        </div>
                      </div>
                      
                      <div className="p-4 rounded-lg border border-slate-200 dark:border-slate-700" style={{ backgroundColor, color: textColor }}>
                        <p className="font-medium">Preview</p>
                        <p className="text-sm">This is how your text will appear in the PDF.</p>
                      </div>
                    </TabsContent>

                    {/* Header/Footer Settings */}
                    <TabsContent value="header" className="space-y-4">
                      <div>
                        <label className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-2 block">Header Text</label>
                        <Input value={headerText} onChange={(e) => setHeaderText(e.target.value)} placeholder="Enter header text (appears at top of each page)" className="bg-white dark:bg-slate-900" />
                      </div>
                      
                      <div>
                        <label className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-2 block">Footer Text</label>
                        <Input value={footerText} onChange={(e) => setFooterText(e.target.value)} placeholder="Enter footer text (appears at bottom of each page)" className="bg-white dark:bg-slate-900" />
                      </div>
                      
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Hash className="w-4 h-4 text-slate-500" />
                          <label className="text-sm font-medium text-slate-700 dark:text-slate-300">Show Page Numbers</label>
                        </div>
                        <Switch checked={showPageNumbers} onCheckedChange={setShowPageNumbers} />
                      </div>
                      
                      {showPageNumbers && (
                        <div>
                          <label className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-2 block">Page Number Position</label>
                          <Select value={pageNumberPosition} onValueChange={setPageNumberPosition}>
                            <SelectTrigger className="bg-white dark:bg-slate-900"><SelectValue /></SelectTrigger>
                            <SelectContent className="dark:bg-slate-800">
                              <SelectItem value="bottom-left">Bottom Left</SelectItem>
                              <SelectItem value="bottom-center">Bottom Center</SelectItem>
                              <SelectItem value="bottom-right">Bottom Right</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      )}
                    </TabsContent>

                    {/* Advanced Settings */}
                    <TabsContent value="advanced" className="space-y-6">
                      {/* Watermark Section */}
                      <div className="space-y-4 p-4 rounded-lg border border-slate-200 dark:border-slate-700">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <Droplet className="w-4 h-4 text-slate-500" />
                            <label className="text-sm font-semibold text-slate-700 dark:text-slate-300">Watermark</label>
                          </div>
                          <Switch checked={watermarkEnabled} onCheckedChange={setWatermarkEnabled} />
                        </div>
                        
                        {watermarkEnabled && (
                          <div className="space-y-4 pt-2">
                            <Input value={watermarkText} onChange={(e) => setWatermarkText(e.target.value)} placeholder="e.g., CONFIDENTIAL, DRAFT" className="bg-white dark:bg-slate-900" />
                            <div className="grid grid-cols-2 gap-4">
                              <div>
                                <div className="flex justify-between items-center mb-2">
                                  <label className="text-sm font-medium text-slate-700 dark:text-slate-300">Opacity</label>
                                  <Badge variant="secondary" className="dark:bg-slate-700">{watermarkOpacity[0]}%</Badge>
                                </div>
                                <Slider value={watermarkOpacity} onValueChange={setWatermarkOpacity} min={10} max={80} step={5} />
                              </div>
                              <div>
                                <div className="flex justify-between items-center mb-2">
                                  <label className="text-sm font-medium text-slate-700 dark:text-slate-300">Angle</label>
                                  <Badge variant="secondary" className="dark:bg-slate-700">{watermarkAngle[0]}°</Badge>
                                </div>
                                <Slider value={watermarkAngle} onValueChange={setWatermarkAngle} min={-90} max={90} step={5} />
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                      
                      {/* Password Protection */}
                      <div className="space-y-4 p-4 rounded-lg border border-slate-200 dark:border-slate-700">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <Lock className="w-4 h-4 text-slate-500" />
                            <label className="text-sm font-semibold text-slate-700 dark:text-slate-300">Password Protection</label>
                          </div>
                          <Switch checked={passwordEnabled} onCheckedChange={setPasswordEnabled} />
                        </div>
                        
                        {passwordEnabled && (
                          <div className="pt-2">
                            <Input type="password" value={pdfPassword} onChange={(e) => setPdfPassword(e.target.value)} placeholder="Enter password to protect PDF" className="bg-white dark:bg-slate-900" />
                            <p className="text-xs text-slate-500 mt-2">Password protected PDFs will require this password to open.</p>
                          </div>
                        )}
                      </div>
                    </TabsContent>
                  </Tabs>
                </CardContent>
              </Card>

              {/* Action Buttons */}
              <div className="flex flex-wrap gap-3 justify-center">
                <Button
                  onClick={handleDownload}
                  disabled={!content.trim() || isGenerating || (passwordEnabled && !pdfPassword)}
                  size="lg"
                  className="rounded-full px-8 bg-emerald-600 hover:bg-emerald-700 shadow-lg shadow-emerald-500/20"
                  data-testid="generate-pdf-button"
                >
                  {isGenerating ? (
                    <><div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2" />Generating...</>
                  ) : (
                    <><Download className="w-5 h-5 mr-2" />Download PDF{passwordEnabled && pdfPassword && <Lock className="w-4 h-4 ml-2" />}</>
                  )}
                </Button>
                <Button onClick={handleReset} variant="outline" size="lg" className="rounded-full px-8">
                  <RotateCcw className="w-5 h-5 mr-2" />Reset
                </Button>
              </div>
            </div>

            {/* Preview Section */}
            {showPreview && (
              <div className="lg:sticky lg:top-24 h-fit">
                <Card className="border-slate-200 dark:border-slate-700 dark:bg-slate-800">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="font-semibold text-slate-900 dark:text-white flex items-center gap-2">
                        <Eye className="w-5 h-5 text-emerald-600" />PDF Preview
                      </h3>
                      <Button variant="ghost" size="sm" onClick={() => setShowPreview(false)}><EyeOff className="w-4 h-4" /></Button>
                    </div>
                    {previewUrl ? (
                      <object data={previewUrl} type="application/pdf" className="w-full h-[600px] border border-slate-200 dark:border-slate-700 rounded-lg bg-white">
                        <embed src={previewUrl} type="application/pdf" className="w-full h-full" />
                        <p className="p-4 text-center">
                          Unable to display PDF preview. 
                          <a href={previewUrl} target="_blank" rel="noopener noreferrer" className="text-emerald-600 underline ml-1">Open in new tab</a>
                        </p>
                      </object>
                    ) : (
                      <div className="w-full h-[600px] border border-slate-200 dark:border-slate-700 rounded-lg bg-slate-100 dark:bg-slate-900 flex items-center justify-center">
                        <p className="text-slate-500 dark:text-slate-400">Click "Preview" to see your document</p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>
            )}
          </div>
        </div>

        {/* Features */}
        <div className="max-w-4xl mx-auto mt-12">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { icon: Type, title: "Rich Text", desc: "Bold, italic, lists", color: "bg-blue-100 dark:bg-blue-900/30", iconColor: "text-blue-600 dark:text-blue-400" },
              { icon: Eye, title: "Live Preview", desc: "See before download", color: "bg-green-100 dark:bg-green-900/30", iconColor: "text-green-600 dark:text-green-400" },
              { icon: Droplet, title: "Watermark", desc: "Add custom watermark", color: "bg-purple-100 dark:bg-purple-900/30", iconColor: "text-purple-600 dark:text-purple-400" },
              { icon: Lock, title: "Password", desc: "Protect your PDF", color: "bg-orange-100 dark:bg-orange-900/30", iconColor: "text-orange-600 dark:text-orange-400" }
            ].map(({ icon: Icon, title, desc, color, iconColor }) => (
              <Card key={title} className="border-slate-200 dark:border-slate-700 dark:bg-slate-800">
                <CardContent className="p-4 text-center">
                  <div className={`w-10 h-10 rounded-full ${color} flex items-center justify-center mx-auto mb-3`}>
                    <Icon className={`w-5 h-5 ${iconColor}`} />
                  </div>
                  <h3 className="font-semibold text-slate-900 dark:text-white text-sm">{title}</h3>
                  <p className="text-xs text-slate-600 dark:text-slate-400">{desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>

        {/* Ad Placement - After Features (Responsive) */}
        <AdContainer className="mt-8">
          <ResponsiveAd slot="after-features" mobileSize="mobile-banner" desktopSize="banner" />
        </AdContainer>
      </main>

      {/* CMS Content */}
      {cmsContent?.content && (
        <div className="bg-slate-50 dark:bg-slate-900 py-12">
          <div className="container mx-auto px-4">
            <RenderCMSContent content={cmsContent.content} />
          </div>
        </div>
      )}

      {/* Ad Placement - Before Footer (Responsive) */}
      <AdContainer className="my-8">
        <ResponsiveAd slot="before-footer" mobileSize="mobile-rectangle" desktopSize="rectangle" />
      </AdContainer>

      {/* Footer */}
      <footer className="border-t border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 py-8 mt-12">
        <div className="container mx-auto px-4 text-center text-sm text-slate-600 dark:text-slate-400">
          <p>&copy; {new Date().getFullYear()} KayTool. All processing happens locally in your browser.</p>
        </div>
      </footer>

      {/* Sticky Mobile Ad */}
      <StickyMobileAd slot="sticky-footer" />

    </div>
  );
}
